import React, { useState } from 'react';
import { 
  Calculator, 
  Clock, 
  Mail, 
  CheckCircle, 
  ArrowRight,
  Users,
  Building2,
  UtensilsCrossed,
  Cake,
  Wine,
  ChefHat,
  Zap,
  TrendingUp,
  MessageSquare,
  Phone,
  Send,
  Star,
  Play,
  Filter,
  BadgeCheck,
  XCircle,
  Sparkles,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

// Enhanced Mini Demo Calculator Component
const MiniDemoCalculator = ({ language = 'fi' }) => {
  const [step, setStep] = useState(0);
  const [guests, setGuests] = useState(50);
  const [expandedWeddingMenu, setExpandedWeddingMenu] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selections, setSelections] = useState({
    eventType: '',
    weddingChoice: null,
    weddingMenu: null,
    salads: [],
    mainCourses: [],
    sides: [],
    breads: [],
    desserts: [],
    drinks: [],
    services: [],
    delivery: null
  });

  // Demo translations
  const dt = {
    fi: {
      title: 'Pitopalvelulaskuri - Demo',
      eventQuestion: 'Millainen tilaisuus?',
      wedding: 'Häät', birthday: 'Syntymäpäivät', memorial: 'Muistotilaisuus', corporate: 'Yritysjuhlat',
      guestsQuestion: 'Montako vierasta?', minGuests: 'Vähintään 10 henkilöä',
      menuChoice: 'Miten rakennetaan menu?', menuChoiceDesc: 'Valitse valmis häämenu tai kokoa itse',
      readyMenu: 'Valmis häämenu', readyMenuDesc: 'Valitse valmiista menupaketeista',
      customMenu: 'Rakennan itse menun', customMenuDesc: 'Valitse ruoat yksitellen',
      selectWeddingMenu: 'Valitse häämenu', weddingMenuDesc: 'Valmiit menuvaihtoehdot häihin',
      selectSalads: 'Valitse salaatit', selectMultiple: 'Voit valita useamman',
      selectMainCourse: 'Valitse pääruoat', mainCourseDesc: 'Valitse kategorioista',
      selectSides: 'Valitse lisukkeet', sidesDesc: 'Voit valita useamman',
      selectBreads: 'Valitse leivät', breadsDesc: 'Voit valita useamman',
      dessertQuestion: 'Jälkiruoat', drinksQuestion: 'Juomat',
      additionalServices: 'Lisäpalvelut', deliveryQuestion: 'Toimitus', deliveryDesc: 'Toimitetaanko ruoat perille?',
      yes: 'Kyllä', no: 'Ei', pickup: 'Nouto', summary: 'Yhteenveto',
      eventType: 'Tilaisuus', guestCount: 'Henkilömäärä', persons: 'hlö', weddingMenu: 'Häämenu',
      salads: 'Salaatit', sides: 'Lisukkeet', breads: 'Leivät', pcs: 'kpl', mainCourse: 'Pääruoat', dessert: 'Jälkiruoat', drinks: 'Juomat', delivery: 'Toimitus',
      total: 'Yhteensä', demoNote: 'Demo - oikea laskuri räätälöidään sinun hinnoitteluusi',
      previous: 'Edellinen', next: 'Seuraava', startOver: 'Aloita alusta',
      pork: 'Possu', beef: 'Nauta', chicken: 'Kana', fish: 'Kala', vegetarian: 'Kasvis',
      starters: 'Alkupalat', mainCoursesLabel: 'Pääruoat', dessertsLabel: 'Jälkiruoat', perPerson: '/hlö', selected: 'Valittu:'
    },
    en: {
      title: 'Catering Calculator - Demo',
      eventQuestion: 'What kind of event?',
      wedding: 'Wedding', birthday: 'Birthday', memorial: 'Memorial', corporate: 'Corporate event',
      guestsQuestion: 'How many guests?', minGuests: 'Minimum 10 people',
      menuChoice: 'How to build the menu?', menuChoiceDesc: 'Choose a ready wedding menu or build your own',
      readyMenu: 'Ready wedding menu', readyMenuDesc: 'Choose from ready menu packages',
      customMenu: 'Build my own menu', customMenuDesc: 'Select dishes individually',
      selectWeddingMenu: 'Select wedding menu', weddingMenuDesc: 'Ready menu options for weddings',
      selectSalads: 'Select salads', selectMultiple: 'You can select multiple',
      selectMainCourse: 'Select main courses', mainCourseDesc: 'Choose from categories',
      selectSides: 'Select sides', sidesDesc: 'You can select multiple',
      selectBreads: 'Select breads', breadsDesc: 'You can select multiple',
      dessertQuestion: 'Desserts', drinksQuestion: 'Drinks',
      additionalServices: 'Additional services', deliveryQuestion: 'Delivery', deliveryDesc: 'Should we deliver the food?',
      yes: 'Yes', no: 'No', pickup: 'Pickup', summary: 'Summary',
      eventType: 'Event', guestCount: 'Guest count', persons: 'ppl', weddingMenu: 'Wedding menu',
      salads: 'Salads', sides: 'Sides', breads: 'Breads', pcs: 'pcs', mainCourse: 'Main courses', dessert: 'Desserts', drinks: 'Drinks', delivery: 'Delivery',
      total: 'Total', demoNote: 'Demo - the real calculator is customized to your pricing',
      previous: 'Previous', next: 'Next', startOver: 'Start over',
      pork: 'Pork', beef: 'Beef', chicken: 'Chicken', fish: 'Fish', vegetarian: 'Vegetarian',
      starters: 'Starters', mainCoursesLabel: 'Main courses', dessertsLabel: 'Desserts', perPerson: '/person', selected: 'Selected:'
    }
  };
  const t = dt[language];

  const prices = {
    salads: { 'vihersalaatti': 3.5, 'caesarsalaatti': 4.5, 'perunasalaatti': 3, 'punajuurisalaatti': 3.5 },
    mains: { 
      'porsaanpaisti': 12.5, 'kyljykset': 13, 'pulled-pork': 11, 'possun-ulkofilee': 14,
      'harkaa-burgundilaisittain': 16.5, 'paahtopaisti': 15, 'naudan-sisafilee': 22, 'lihapullat': 11,
      'kanan-koipireisi': 12.5, 'kanaa-kerma': 12, 'broilerin-rinta': 13, 'kalkkunaa': 14,
      'lohta-sitruuna': 16.5, 'siika': 15, 'kuha': 17, 'kirjolohi': 14,
      'kasvislasagne': 10.5, 'taytettya-paprikaa': 11, 'falafel': 10, 'kasviskeitto': 9
    },
    sides: { 'keitetyt-perunat': 2, 'valkosipuliperunat': 2.5, 'lohkoperunat': 2.5, 'kauden-kasvikset': 3 },
    breads: { 'ruisleipa': 1.5, 'patonki': 2, 'focaccia': 2.5, 'saaristolaisleipa': 2 },
    desserts: { 'mokkapala': 2, 'taytekakku': 4.5, 'juustokakku': 4.5, 'munkkibuffa': 3, 'pannukakku': 3, 'marjakiisseli': 2.5 },
    drinks: { 'mehu': 1, 'kotikalja': 1, 'kahvi': 2.5, 'tee': 2 },
    services: { 'tarjoilu': 200, 'kattaus': 1.5, 'astiat': 3 },
    delivery: 50
  };

  // Main course categories with dishes
  const mainCourseCategories = [
    { 
      id: 'pork', 
      label: t.pork,
      dishes: [
        { value: 'porsaanpaisti', label: language === 'fi' ? 'Porsaanpaisti' : 'Pork roast' },
        { value: 'kyljykset', label: language === 'fi' ? 'Kyljykset' : 'Pork chops' },
        { value: 'pulled-pork', label: 'Pulled pork' },
        { value: 'possun-ulkofilee', label: language === 'fi' ? 'Possun ulkofilee' : 'Pork tenderloin' }
      ]
    },
    { 
      id: 'beef', 
      label: t.beef,
      dishes: [
        { value: 'harkaa-burgundilaisittain', label: language === 'fi' ? 'Härkää burgundilaisittain' : 'Beef Burgundy' },
        { value: 'paahtopaisti', label: language === 'fi' ? 'Paahtopaisti' : 'Roast beef' },
        { value: 'naudan-sisafilee', label: language === 'fi' ? 'Naudan sisäfilee' : 'Beef tenderloin' },
        { value: 'lihapullat', label: language === 'fi' ? 'Lihapullat' : 'Meatballs' }
      ]
    },
    { 
      id: 'chicken', 
      label: t.chicken,
      dishes: [
        { value: 'kanan-koipireisi', label: language === 'fi' ? 'Kanan koipireisi' : 'Chicken thigh' },
        { value: 'kanaa-kerma', label: language === 'fi' ? 'Kanaa kermakastikkeessa' : 'Creamy chicken' },
        { value: 'broilerin-rinta', label: language === 'fi' ? 'Broilerin rintafilee' : 'Chicken breast' },
        { value: 'kalkkunaa', label: language === 'fi' ? 'Kalkkunaa' : 'Turkey' }
      ]
    },
    { 
      id: 'fish', 
      label: t.fish,
      dishes: [
        { value: 'lohta-sitruuna', label: language === 'fi' ? 'Lohta sitruunakastikkeella' : 'Salmon with lemon' },
        { value: 'siika', label: language === 'fi' ? 'Siikaa voikastikkeella' : 'Whitefish with butter' },
        { value: 'kuha', label: language === 'fi' ? 'Kuhaa tillikastikkeella' : 'Pike-perch with dill' },
        { value: 'kirjolohi', label: language === 'fi' ? 'Kirjolohta' : 'Rainbow trout' }
      ]
    },
    { 
      id: 'vegetarian', 
      label: t.vegetarian,
      dishes: [
        { value: 'kasvislasagne', label: language === 'fi' ? 'Kasvislasagne' : 'Vegetable lasagna' },
        { value: 'taytettya-paprikaa', label: language === 'fi' ? 'Täytettyä paprikaa' : 'Stuffed peppers' },
        { value: 'falafel', label: 'Falafel' },
        { value: 'kasviskeitto', label: language === 'fi' ? 'Kasviskeitto' : 'Vegetable soup' }
      ]
    }
  ];

  // Wedding menu packages
  const weddingMenus = [
    { id: 'haamenu1', name: language === 'fi' ? 'Häämenu 1 - Klassinen' : 'Wedding Menu 1 - Classic', pricePerPerson: 55, description: language === 'fi' ? 'Perinteinen ja tyylikäs' : 'Traditional and elegant',
      items: { alkupalat: ['Savulohimousse', 'Kauden salaatti'], paaruoat: ['Paahtopaisti', 'Kermaperunat', 'Kauden kasvikset'], jalkiruoat: ['Suklaakakku', 'Kahvi'] }
    },
    { id: 'haamenu2', name: language === 'fi' ? 'Häämenu 2 - Moderni' : 'Wedding Menu 2 - Modern', pricePerPerson: 65, description: language === 'fi' ? 'Raikas ja nykyaikainen' : 'Fresh and contemporary',
      items: { alkupalat: ['Burrata', 'Vihersalaatti'], paaruoat: ['Kanan koipireisi', 'Tryffelirisotto'], jalkiruoat: ['Sitruunatartaletti', 'Kahvi'] }
    },
    { id: 'haamenu3', name: language === 'fi' ? 'Häämenu 3 - Premium' : 'Wedding Menu 3 - Premium', pricePerPerson: 79, description: language === 'fi' ? 'Ylellinen juhla-ateria' : 'Luxurious celebration meal',
      items: { alkupalat: ['Graavilohi', 'Katkarapusalaatti'], paaruoat: ['Naudan sisäfilee', 'Duchesse-perunat'], jalkiruoat: ['Crème brûlée', 'Juustolautanen'] }
    },
    { id: 'haamenu4', name: language === 'fi' ? 'Häämenu 4 - Kasvis' : 'Wedding Menu 4 - Vegetarian', pricePerPerson: 52, description: language === 'fi' ? 'Kasvisruokailijan juhlamenu' : 'Vegetarian celebration menu',
      items: { alkupalat: ['Vuohenjuusto-tartar', 'Quinoa-salaatti'], paaruoat: ['Portobello-sienet', 'Bataattigratiini'], jalkiruoat: ['Suklaatartaletti', 'Marjakiisseli'] }
    }
  ];

  const eventTypes = [
    { value: 'haat', label: t.wedding },
    { value: 'synttarit', label: t.birthday },
    { value: 'muistotilaisuus', label: t.memorial },
    { value: 'yritysjuhlat', label: t.corporate }
  ];

  const saladOptions = [
    { value: 'vihersalaatti', label: language === 'fi' ? 'Vihersalaatti' : 'Green salad' },
    { value: 'caesarsalaatti', label: language === 'fi' ? 'Caesar-salaatti' : 'Caesar salad' },
    { value: 'perunasalaatti', label: language === 'fi' ? 'Perunasalaatti' : 'Potato salad' },
    { value: 'punajuurisalaatti', label: language === 'fi' ? 'Punajuurisalaatti' : 'Beetroot salad' }
  ];

  const dessertOptions = [
    { value: 'mokkapala', label: language === 'fi' ? 'Mokkapala' : 'Mocha square' },
    { value: 'taytekakku', label: language === 'fi' ? 'Täytekakku' : 'Layer cake' },
    { value: 'juustokakku', label: language === 'fi' ? 'Juustokakku' : 'Cheesecake' },
    { value: 'munkkibuffa', label: language === 'fi' ? 'Munkkibuffa' : 'Donut buffet' },
    { value: 'pannukakku', label: language === 'fi' ? 'Pannukakku' : 'Pancake' },
    { value: 'marjakiisseli', label: language === 'fi' ? 'Marjakiisseli' : 'Berry pudding' }
  ];

  const sideOptions = [
    { value: 'keitetyt-perunat', label: language === 'fi' ? 'Keitetyt perunat' : 'Boiled potatoes' },
    { value: 'valkosipuliperunat', label: language === 'fi' ? 'Valkosipuliperunat' : 'Garlic potatoes' },
    { value: 'lohkoperunat', label: language === 'fi' ? 'Lohkoperunat' : 'Wedge potatoes' },
    { value: 'kauden-kasvikset', label: language === 'fi' ? 'Kauden kasvikset' : 'Seasonal vegetables' }
  ];

  const breadOptions = [
    { value: 'ruisleipa', label: language === 'fi' ? 'Ruisleipä' : 'Rye bread' },
    { value: 'patonki', label: language === 'fi' ? 'Patonki' : 'Baguette' },
    { value: 'focaccia', label: 'Focaccia' },
    { value: 'saaristolaisleipa', label: language === 'fi' ? 'Saaristolaisleipä' : 'Archipelago bread' }
  ];

  const drinkOptions = [
    { value: 'mehu', label: language === 'fi' ? 'Mehu' : 'Juice' },
    { value: 'kotikalja', label: language === 'fi' ? 'Kotikalja' : 'Home brew' },
    { value: 'kahvi', label: language === 'fi' ? 'Kahvi' : 'Coffee' },
    { value: 'tee', label: language === 'fi' ? 'Tee' : 'Tea' }
  ];

  const serviceOptions = [
    { id: 'tarjoilu', label: language === 'fi' ? 'Tarjoiluhenkilökunta' : 'Service staff' },
    { id: 'kattaus', label: language === 'fi' ? 'Pöytienkattaus' : 'Table setting' },
    { id: 'astiat', label: language === 'fi' ? 'Vuokra-astiat' : 'Rental dishes' }
  ];

  const toggleSalad = (saladValue) => {
    setSelections(prev => ({
      ...prev,
      salads: prev.salads.includes(saladValue)
        ? prev.salads.filter(s => s !== saladValue)
        : [...prev.salads, saladValue]
    }));
  };

  const toggleMainCourse = (courseValue) => {
    setSelections(prev => ({
      ...prev,
      mainCourses: prev.mainCourses.includes(courseValue)
        ? prev.mainCourses.filter(c => c !== courseValue)
        : [...prev.mainCourses, courseValue]
    }));
  };

  const toggleDessert = (dessertValue) => {
    setSelections(prev => ({
      ...prev,
      desserts: prev.desserts.includes(dessertValue)
        ? prev.desserts.filter(d => d !== dessertValue)
        : [...prev.desserts, dessertValue]
    }));
  };

  const toggleDrink = (drinkValue) => {
    setSelections(prev => ({
      ...prev,
      drinks: prev.drinks.includes(drinkValue)
        ? prev.drinks.filter(d => d !== drinkValue)
        : [...prev.drinks, drinkValue]
    }));
  };

  const toggleSide = (sideValue) => {
    setSelections(prev => ({
      ...prev,
      sides: prev.sides.includes(sideValue)
        ? prev.sides.filter(s => s !== sideValue)
        : [...prev.sides, sideValue]
    }));
  };

  const toggleBread = (breadValue) => {
    setSelections(prev => ({
      ...prev,
      breads: prev.breads.includes(breadValue)
        ? prev.breads.filter(b => b !== breadValue)
        : [...prev.breads, breadValue]
    }));
  };

  const toggleService = (serviceId) => {
    setSelections(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const calculateTotal = () => {
    let total = 0;
    
    // Wedding menu - fixed price per person (only if ready menu chosen)
    if (selections.eventType === 'haat' && selections.weddingChoice === 'ready' && selections.weddingMenu) {
      const selectedMenu = weddingMenus.find(m => m.id === selections.weddingMenu);
      if (selectedMenu) {
        total += selectedMenu.pricePerPerson * guests;
      }
    } else {
      // Regular pricing (for non-weddings OR weddings with custom menu)
      // Salads
      selections.salads.forEach(salad => {
        if (prices.salads[salad]) {
          total += prices.salads[salad] * guests;
        }
      });
      // Main courses (multiple)
      selections.mainCourses.forEach(course => {
        if (prices.mains[course]) {
          total += prices.mains[course] * guests;
        }
      });
      // Sides (multiple)
      selections.sides.forEach(side => {
        if (prices.sides[side]) {
          total += prices.sides[side] * guests;
        }
      });
      // Breads (multiple)
      selections.breads.forEach(bread => {
        if (prices.breads[bread]) {
          total += prices.breads[bread] * guests;
        }
      });
      // Desserts (multiple)
      selections.desserts.forEach(dessert => {
        if (prices.desserts[dessert]) {
          total += prices.desserts[dessert] * guests;
        }
      });
      // Drinks (multiple)
      selections.drinks.forEach(drink => {
        if (prices.drinks[drink]) {
          total += prices.drinks[drink] * guests;
        }
      });
    }
    
    // Services (for both wedding and regular)
    selections.services.forEach(service => {
      if (service === 'tarjoilu') {
        total += prices.services.tarjoilu;
      } else {
        total += prices.services[service] * guests;
      }
    });
    // Delivery (for both)
    if (selections.delivery === true) {
      total += prices.delivery;
    }
    return total;
  };

  // All possible steps
  const allSteps = [
    { id: 'event', title: 'Tilaisuus' },
    { id: 'guests', title: 'Vieraat' },
    { id: 'weddingChoice', title: 'Menuvalinta', weddingOnly: true },
    { id: 'weddingMenu', title: 'Häämenu', weddingReadyOnly: true },
    { id: 'salads', title: 'Salaatit', regularOnly: true },
    { id: 'main', title: 'Pääruoat', regularOnly: true },
    { id: 'sides', title: 'Lisukkeet', regularOnly: true },
    { id: 'breads', title: 'Leivät', regularOnly: true },
    { id: 'desserts', title: 'Jälkiruoat', regularOnly: true },
    { id: 'drinks', title: 'Juomat', regularOnly: true },
    { id: 'services', title: 'Lisäpalvelut' },
    { id: 'delivery', title: 'Toimitus' },
    { id: 'summary', title: 'Yhteenveto' }
  ];

  // Dynamic steps based on event type and wedding choice
  const steps = allSteps.filter(s => {
    // Wedding with ready menu
    if (selections.eventType === 'haat' && selections.weddingChoice === 'ready') {
      return !s.regularOnly; // Show wedding steps, hide regular food steps
    }
    // Wedding with custom menu (same as regular events + weddingChoice)
    if (selections.eventType === 'haat' && selections.weddingChoice === 'custom') {
      return !s.weddingReadyOnly; // Hide only the ready wedding menu step
    }
    // Wedding but choice not yet made
    if (selections.eventType === 'haat') {
      return s.id === 'event' || s.id === 'guests' || s.id === 'weddingChoice';
    }
    // Regular events
    return !s.weddingOnly && !s.weddingReadyOnly;
  });

  const currentStepId = steps[step]?.id;

  const canProceed = () => {
    switch(currentStepId) {
      case 'event': return selections.eventType !== '';
      case 'guests': return guests >= 10;
      case 'weddingChoice': return selections.weddingChoice !== null;
      case 'weddingMenu': return selections.weddingMenu !== null;
      case 'main': return selections.mainCourses.length > 0;
      case 'delivery': return selections.delivery !== null;
      default: return true;
    }
  };

  const resetForm = () => {
    setStep(0);
    setSelections({
      eventType: '',
      weddingChoice: null,
      weddingMenu: null,
      salads: [],
      mainCourses: [],
      sides: [],
      breads: [],
      desserts: [],
      drinks: [],
      services: [],
      delivery: null
    });
    setExpandedWeddingMenu(null);
    setExpandedCategory(null);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-amber-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between text-white">
          <span className="text-xs sm:text-sm font-medium">{t.title}</span>
          <span className="text-xs sm:text-sm bg-white/20 px-2 sm:px-3 py-1 rounded-full">
            {step + 1}/{steps.length}
          </span>
        </div>
        <div className="mt-2 h-1.5 bg-white/30 rounded-full">
          <div 
            className="h-full bg-white rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {/* Event Type */}
        {currentStepId === 'event' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.eventQuestion}</h3>
            <div className="grid grid-cols-2 gap-2">
              {eventTypes.map(event => (
                <button
                  key={event.value}
                  onClick={() => setSelections(prev => ({ ...prev, eventType: event.value, weddingMenu: null }))}
                  className={`p-2 sm:p-3 rounded-xl border text-center transition-all cursor-pointer text-xs sm:text-sm ${
                    selections.eventType === event.value
                      ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                      : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                >
                  {event.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Guests */}
        {currentStepId === 'guests' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.guestsQuestion}</h3>
            <div className="flex items-center gap-3 sm:gap-4">
              <input
                type="range"
                min="10"
                max="200"
                value={guests}
                onChange={(e) => setGuests(parseInt(e.target.value))}
                className="flex-1 accent-amber-500"
              />
              <span className="text-xl sm:text-2xl font-bold text-amber-600 w-12 sm:w-16 text-center">{guests}</span>
            </div>
            <p className="text-xs sm:text-sm text-stone-500">{t.minGuests}</p>
          </div>
        )}

        {/* Wedding Choice - Ready menu or custom */}
        {currentStepId === 'weddingChoice' && (
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.menuChoice}</h3>
            </div>
            <p className="text-xs sm:text-sm text-stone-500">{t.menuChoiceDesc}</p>
            <div className="space-y-3">
              <button
                onClick={() => setSelections(prev => ({ ...prev, weddingChoice: 'ready' }))}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                  selections.weddingChoice === 'ready'
                    ? 'border-amber-500 bg-amber-50 shadow-lg'
                    : 'border-stone-200 hover:border-amber-300 hover:bg-amber-50/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selections.weddingChoice === 'ready' ? 'bg-amber-500' : 'bg-stone-100'
                  }`}>
                    <Sparkles className={`w-5 h-5 ${selections.weddingChoice === 'ready' ? 'text-white' : 'text-stone-400'}`} />
                  </div>
                  <div>
                    <span className={`font-bold block ${selections.weddingChoice === 'ready' ? 'text-amber-700' : 'text-stone-800'}`}>
                      {t.readyMenu}
                    </span>
                    <p className="text-xs text-stone-500">{t.readyMenuDesc}</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => setSelections(prev => ({ ...prev, weddingChoice: 'custom', weddingMenu: null }))}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                  selections.weddingChoice === 'custom'
                    ? 'border-amber-500 bg-amber-50 shadow-lg'
                    : 'border-stone-200 hover:border-amber-300 hover:bg-amber-50/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selections.weddingChoice === 'custom' ? 'bg-amber-500' : 'bg-stone-100'
                  }`}>
                    <UtensilsCrossed className={`w-5 h-5 ${selections.weddingChoice === 'custom' ? 'text-white' : 'text-stone-400'}`} />
                  </div>
                  <div>
                    <span className={`font-bold block ${selections.weddingChoice === 'custom' ? 'text-amber-700' : 'text-stone-800'}`}>
                      {t.customMenu}
                    </span>
                    <p className="text-xs text-stone-500">{t.customMenuDesc}</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Wedding Menu - Only for weddings with ready menu choice */}
        {currentStepId === 'weddingMenu' && (
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.selectWeddingMenu}</h3>
            </div>
            <p className="text-xs sm:text-sm text-stone-500">{t.weddingMenuDesc}</p>
            <div className="space-y-3">
              {weddingMenus.map((menu) => {
                const isSelected = selections.weddingMenu === menu.id;
                const isExpanded = expandedWeddingMenu === menu.id;
                return (
                  <div key={menu.id} className={`rounded-xl border-2 transition-all overflow-hidden ${
                    isSelected ? 'border-amber-500 bg-amber-50 shadow-lg' : 'border-stone-200'
                  }`}>
                    <button
                      onClick={() => {
                        setSelections(prev => ({ ...prev, weddingMenu: menu.id }));
                        setExpandedWeddingMenu(isExpanded ? null : menu.id);
                      }}
                      className="w-full p-3 sm:p-4 flex items-center justify-between text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <span className={`font-bold text-sm sm:text-base block ${isSelected ? 'text-amber-700' : 'text-stone-800'}`}>
                          {menu.name}
                        </span>
                        <p className="text-xs sm:text-sm text-stone-500 mt-0.5">{menu.description}</p>
                        <p className="text-xs text-amber-600 font-semibold mt-1">{menu.pricePerPerson}€{t.perPerson}</p>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 ml-2">
                        <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 text-stone-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-amber-500 bg-amber-500' : 'border-stone-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />}
                        </div>
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-stone-200 bg-white/50">
                        <div className="pt-3 sm:pt-4 space-y-2 sm:space-y-3">
                          <div>
                            <h4 className="font-semibold text-amber-700 text-xs uppercase tracking-wide">Alkupalat</h4>
                            <ul className="mt-1 text-stone-600 text-xs sm:text-sm">
                              {menu.items.alkupalat.map((item, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-amber-400 rounded-full flex-shrink-0"></span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-semibold text-amber-700 text-xs uppercase tracking-wide">Pääruoat</h4>
                            <ul className="mt-1 text-stone-600 text-xs sm:text-sm">
                              {menu.items.paaruoat.map((item, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-amber-400 rounded-full flex-shrink-0"></span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-semibold text-amber-700 text-xs uppercase tracking-wide">Jälkiruoat</h4>
                            <ul className="mt-1 text-stone-600 text-xs sm:text-sm">
                              {menu.items.jalkiruoat.map((item, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-amber-400 rounded-full flex-shrink-0"></span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Salads - Regular events only */}
        {currentStepId === 'salads' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.selectSalads}</h3>
            <p className="text-xs sm:text-sm text-stone-500">{t.selectMultiple}</p>
            <div className="grid grid-cols-2 gap-2">
              {saladOptions.map(salad => (
                <button
                  key={salad.value}
                  onClick={() => toggleSalad(salad.value)}
                  className={`p-2 sm:p-3 rounded-xl border text-center transition-all cursor-pointer text-xs sm:text-sm ${
                    selections.salads.includes(salad.value)
                      ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                      : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                >
                  {salad.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Courses - Accordion style with categories */}
        {currentStepId === 'main' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.selectMainCourse}</h3>
            <p className="text-xs sm:text-sm text-stone-500">{t.mainCourseDesc} - {t.selectMultiple}</p>
            <div className="space-y-2">
              {mainCourseCategories.map(category => {
                const isExpanded = expandedCategory === category.id;
                const selectedFromCategory = selections.mainCourses.filter(c => 
                  category.dishes.some(d => d.value === c)
                );
                return (
                  <div key={category.id} className={`rounded-xl border-2 transition-all overflow-hidden ${
                    selectedFromCategory.length > 0 ? 'border-amber-500 bg-amber-50/50' : 'border-stone-200'
                  }`}>
                    <button
                      onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                      className="w-full p-3 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${selectedFromCategory.length > 0 ? 'text-amber-700' : 'text-stone-800'}`}>
                          {category.label}
                        </span>
                        {selectedFromCategory.length > 0 && (
                          <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                            {selectedFromCategory.length} {t.pcs}
                          </span>
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-stone-200 bg-white/50">
                        <div className="pt-3 grid grid-cols-1 gap-2">
                          {category.dishes.map(dish => (
                            <button
                              key={dish.value}
                              onClick={() => toggleMainCourse(dish.value)}
                              className={`p-2 rounded-lg border text-left transition-all cursor-pointer text-xs sm:text-sm ${
                                selections.mainCourses.includes(dish.value)
                                  ? 'border-amber-500 bg-amber-100 text-stone-800 font-medium'
                                  : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>{dish.label}</span>
                                {selections.mainCourses.includes(dish.value) && (
                                  <CheckCircle className="w-4 h-4 text-amber-600" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selections.mainCourses.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-2 text-xs text-amber-700">
                <span className="font-medium">{t.selected} </span>
                {selections.mainCourses.map(c => {
                  const dish = mainCourseCategories.flatMap(cat => cat.dishes).find(d => d.value === c);
                  return dish?.label;
                }).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Sides - Multiple selection */}
        {currentStepId === 'sides' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.selectSides}</h3>
            <p className="text-xs sm:text-sm text-stone-500">{t.sidesDesc}</p>
            <div className="grid grid-cols-2 gap-2">
              {sideOptions.map(side => (
                <button
                  key={side.value}
                  onClick={() => toggleSide(side.value)}
                  className={`p-2 sm:p-3 rounded-xl border text-center transition-all cursor-pointer text-xs sm:text-sm ${
                    selections.sides.includes(side.value)
                      ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                      : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                >
                  {side.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Breads - Multiple selection */}
        {currentStepId === 'breads' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.selectBreads}</h3>
            <p className="text-xs sm:text-sm text-stone-500">{t.breadsDesc}</p>
            <div className="grid grid-cols-2 gap-2">
              {breadOptions.map(bread => (
                <button
                  key={bread.value}
                  onClick={() => toggleBread(bread.value)}
                  className={`p-2 sm:p-3 rounded-xl border text-center transition-all cursor-pointer text-xs sm:text-sm ${
                    selections.breads.includes(bread.value)
                      ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                      : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                >
                  {bread.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Desserts - Multiple selection */}
        {currentStepId === 'desserts' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.dessertQuestion}</h3>
            <p className="text-xs sm:text-sm text-stone-500">{t.selectMultiple}</p>
            <div className="grid grid-cols-2 gap-2">
              {dessertOptions.map(dessert => (
                <button
                  key={dessert.value}
                  onClick={() => toggleDessert(dessert.value)}
                  className={`p-2 sm:p-3 rounded-xl border text-center transition-all cursor-pointer text-xs sm:text-sm ${
                    selections.desserts.includes(dessert.value)
                      ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                      : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                >
                  {dessert.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Drinks - Multiple selection */}
        {currentStepId === 'drinks' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.drinksQuestion}</h3>
            <p className="text-xs sm:text-sm text-stone-500">{t.selectMultiple}</p>
            <div className="grid grid-cols-2 gap-2">
              {drinkOptions.map(drink => (
                <button
                  key={drink.value}
                  onClick={() => toggleDrink(drink.value)}
                  className={`p-2 sm:p-3 rounded-xl border text-center transition-all cursor-pointer text-xs sm:text-sm ${
                    selections.drinks.includes(drink.value)
                      ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                      : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                >
                  {drink.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Services - Both wedding and regular */}
        {currentStepId === 'services' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.additionalServices}</h3>
            <div className="space-y-2">
              {serviceOptions.map(service => (
                <button
                  key={service.id}
                  onClick={() => toggleService(service.id)}
                  className={`w-full p-2 sm:p-3 rounded-xl border text-center transition-all cursor-pointer text-xs sm:text-sm ${
                    selections.services.includes(service.id)
                      ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                      : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                >
                  {service.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Delivery - Both wedding and regular */}
        {currentStepId === 'delivery' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.deliveryQuestion}</h3>
            <p className="text-xs sm:text-sm text-stone-500">{t.deliveryDesc}</p>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <button
                onClick={() => setSelections(prev => ({ ...prev, delivery: true }))}
                className={`p-3 sm:p-4 rounded-xl border text-center transition-all cursor-pointer ${
                  selections.delivery === true
                    ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                    : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                }`}
              >
                <span className="block text-sm sm:text-lg mb-1">{t.yes}</span>
                <span className="text-xs text-stone-500">+50€</span>
              </button>
              <button
                onClick={() => setSelections(prev => ({ ...prev, delivery: false }))}
                className={`p-3 sm:p-4 rounded-xl border text-center transition-all cursor-pointer ${
                  selections.delivery === false
                    ? 'border-amber-500 bg-amber-50 text-stone-800 font-medium'
                    : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-amber-300 hover:bg-amber-50/50'
                }`}
              >
                <span className="block text-sm sm:text-lg mb-1">{t.no}</span>
                <span className="text-xs text-stone-500">{t.pickup}</span>
              </button>
            </div>
          </div>
        )}

        {/* Summary */}
        {currentStepId === 'summary' && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-stone-800 text-sm sm:text-base">{t.summary}</h3>
            <div className="bg-amber-50 rounded-xl p-3 sm:p-4 space-y-2 border border-amber-100">
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-stone-600">{t.eventType}</span>
                <span className="text-stone-800 font-medium">
                  {eventTypes.find(e => e.value === selections.eventType)?.label || '-'}
                </span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-stone-600">{t.guestCount}</span>
                <span className="text-stone-800 font-medium">{guests} {t.persons}</span>
              </div>
              {selections.eventType === 'haat' && selections.weddingChoice === 'ready' && selections.weddingMenu && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-stone-600">{t.weddingMenu}</span>
                  <span className="text-stone-800 font-medium">
                    {weddingMenus.find(m => m.id === selections.weddingMenu)?.name || '-'}
                  </span>
                </div>
              )}
              {(selections.eventType !== 'haat' || selections.weddingChoice === 'custom') && selections.salads.length > 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-stone-600">{t.salads}</span>
                  <span className="text-stone-800 font-medium">{selections.salads.length} {t.pcs}</span>
                </div>
              )}
              {(selections.eventType !== 'haat' || selections.weddingChoice === 'custom') && selections.mainCourses.length > 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-stone-600">{t.mainCourse}</span>
                  <span className="text-stone-800 font-medium">{selections.mainCourses.length} {t.pcs}</span>
                </div>
              )}
              {(selections.eventType !== 'haat' || selections.weddingChoice === 'custom') && selections.sides.length > 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-stone-600">{t.sides}</span>
                  <span className="text-stone-800 font-medium">{selections.sides.length} {t.pcs}</span>
                </div>
              )}
              {(selections.eventType !== 'haat' || selections.weddingChoice === 'custom') && selections.breads.length > 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-stone-600">{t.breads}</span>
                  <span className="text-stone-800 font-medium">{selections.breads.length} {t.pcs}</span>
                </div>
              )}
              {(selections.eventType !== 'haat' || selections.weddingChoice === 'custom') && selections.desserts.length > 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-stone-600">{t.dessert}</span>
                  <span className="text-stone-800 font-medium">{selections.desserts.length} {t.pcs}</span>
                </div>
              )}
              {(selections.eventType !== 'haat' || selections.weddingChoice === 'custom') && selections.drinks.length > 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-stone-600">{t.drinks}</span>
                  <span className="text-stone-800 font-medium">{selections.drinks.length} {t.pcs}</span>
                </div>
              )}
              {selections.services.length > 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-stone-600">{t.additionalServices}</span>
                  <span className="text-stone-800 font-medium">{selections.services.length} {t.pcs}</span>
                </div>
              )}
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-stone-600">{t.delivery}</span>
                <span className="text-stone-800 font-medium">
                  {selections.delivery === true ? `${t.yes} (+50€)` : t.pickup}
                </span>
              </div>
              <div className="border-t border-amber-200 pt-2 sm:pt-3 mt-2 sm:mt-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-stone-800 text-sm sm:text-base">{t.total}</span>
                  <span className="text-xl sm:text-2xl font-bold text-amber-600">{calculateTotal().toFixed(0)}€</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-stone-500 text-center">
              {t.demoNote}
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-4 sm:mt-6">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-stone-500 hover:text-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t.previous}
          </button>
          {step < steps.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-1 sm:gap-2 transition-colors"
            >
              {t.next}
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          ) : (
            <button
              onClick={resetForm}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm cursor-pointer transition-colors"
            >
              {t.startOver}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const JuhlaLaskuriLandingPage = () => {
  const [language, setLanguage] = useState('fi');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const logoUrl = "https://customer-assets.emergentagent.com/job_cad0ce98-ce33-4181-bcdd-0bdaefaaca63/artifacts/km2fbjmv_Logo-Juhlalaskuri-scalable.svg";

  // Translations
  const t = {
    fi: {
      // Navigation
      demo: 'Demo',
      features: 'Ominaisuudet',
      pricing: 'Hinnoittelu',
      contact: 'Ota yhteyttä',
      
      // Hero
      badge: 'Pitopalveluiden hintalaskuri',
      heroTitle1: 'Vähemmän hintakyselyjä,',
      heroTitle2: 'enemmän valmiiksi lämmitettyjä tarjouspyyntöjä.',
      heroDesc1: 'JuhlaLaskuri automatisoi tarjouslaskentasi. Asiakkaasi rakentavat itse menun ja näkevät hinnan heti – sinä saat vain',
      heroDesc2: 'todelliset tilaukset',
      heroDesc3: ', et turhia kyselyitä.',
      heroSubtext: 'Hoitaa yleisimmät tilaisuudet automaattisesti – ja mukautuu täysin teidän omaan hinnoitteluun.',
      tryDemo: 'Kokeile demoa',
      bookDemo: 'Tilaa JuhlaLaskuri',
      
      // Pain points
      painTitle: 'Tuntuuko tämä tutulta?',
      pain1Title: 'Sähköpostit vievät aikaa',
      pain1Desc: '"Paljonko maksaisi 50 hengen häät?" - Taas tunti menee tarjouksen laskemiseen.',
      pain2Title: 'Turhat kyselyt',
      pain2Desc: '80% kyselyistä ei johda mihinkään - aikaa menee hukkaan.',
      pain3Title: 'Hinnoittelu on hankalaa',
      pain3Desc: 'Joka tilaisuus on erilainen - miten hinnoitella selkeästi?',
      painFooter: 'Kaikki tilaisuudet eivät ole samanlaisia – siksi JuhlaLaskuri keskittyy yleisimpiin keisseihin ja jättää erikoisemmat teille.',
      
      // Solution
      solutionTitle: 'Ratkaisu:',
      solutionSubtitle: 'Anna asiakkaan laskea itse',
      solutionDesc: 'JuhlaLaskuri on sinun yrityksesi näköinen hintalaskuri, joka',
      solutionBold: 'karsii turhat kyselyt pois.',
      solutionHow: 'Miten se toimii?',
      step1: 'Asiakas valitsee ruoat ja palvelut laskurista',
      step2: 'Näkee hinnan heti - ei yllätyksiä',
      step3: 'Vain tosissaan olevat lähettävät tarjouspyynnön',
      step4: 'Sinä saat valmiiksi lasketun tilauksen',
      
      // Target customers
      targetTitle: 'Kenelle JuhlaLaskuri sopii?',
      targetFooter: 'Toimii parhaiten yrityksille, joilla on selkeä hinnoittelu, valmiita palvelukokonaisuuksia tai toistuvia asiakastarpeita.',
      customer1Title: 'Pitopalveluyritykset',
      customer1Desc: 'Juhla- ja tilaustarjoilut',
      customer2Title: 'Juhlatilat & Kartanot',
      customer2Desc: 'Häät, juhlat, kokoukset',
      customer3Title: 'Tilausravintolat',
      customer3Desc: 'Yksityistilaisuudet',
      customer4Title: 'Kahvilat & Leipomot',
      customer4Desc: 'Tilaustarjoilut ja kakut',
      customer5Title: 'Hotellit',
      customer5Desc: 'Kokous- ja juhlapalvelut',
      customer6Title: 'Ruokapalvelut',
      customer6Desc: 'B2B lounas ja kokoustarjoilu',
      
      // Features
      featuresTitle: 'Miksi JuhlaLaskuri?',
      feature1Title: 'Säästä aikaa',
      feature1Desc: 'Automatisoi tarjouslaskenta - säästä tunti per tarjouspyyntö',
      feature2Title: 'Karsii turhat kyselyt',
      feature2Desc: 'Asiakas näkee hinnan heti ja päättää itse jatkaako - et tuhlaa aikaa turhiin tarjouksiin',
      feature3Title: 'Mukautuu teidän hinnoitteluun',
      feature3Desc: 'Laskuri rakennetaan täysin teidän menujen ja hintojen mukaan, joten se toimii kuten teidän normaali hinnoittelu',
      feature4Title: 'Lisää myyntiä',
      feature4Desc: 'Helppo ostopolku = enemmän tilauksia',
      feature5Title: 'Lopeta sähköpostirumba',
      feature5Desc: 'Ei enää "paljonko maksaa?" -kyselyitä - hinta näkyy heti',
      feature6Title: 'Ammattimainen vaikutelma',
      feature6Desc: 'Moderni työkalu erottaa sinut kilpailijoista',
      
      // Pricing
      pricingTitle: 'Selkeä hinnoittelu',
      pricingSubtitle: 'Ei piilokustannuksia, ei yllätyksiä',
      pricingIntro: 'Käyttöönotto on nopeaa, ja laskuri rakennetaan täysin teidän tuotteilla ja hinnoilla.',
      monthly: 'Kuukausi',
      yearly: 'Vuosi',
      currency: '€',
      perMonth: '/kk',
      perYear: '/v',
      setupFee: 'Aloitus ja räätälöinti',
      setupFeeAmount: '299€',
      pricingIncludes: 'Sisältää:',
      pricingItem1: 'Räätälöity laskuri yrityksellesi',
      pricingItem2: 'Oma hinnoittelu ja menurakenne',
      pricingItem3: 'Logot ja brändivärit',
      pricingItem4: 'Tarjouspyynnöt sähköpostiin',
      pricingItem5: 'Hallintapaneeli',
      pricingItem6: 'Tekninen tuki',
      getStarted: 'Aloita nyt',
      save: 'Säästä',
      
      // Contact
      contactTitle: 'Kiinnostuitko?',
      contactSubtitle: 'Varaa maksuton esittely tai kysy lisää',
      contactIntro: 'Saat JuhlaLaskurin käyttöösi täysin teidän menuilla ja hinnoittelulla – valmiina käyttöön.',
      nameLabel: 'Nimi',
      namePlaceholder: 'Etunimi Sukunimi',
      companyLabel: 'Yritys',
      companyPlaceholder: 'Yrityksen nimi',
      emailLabel: 'Sähköposti',
      emailPlaceholder: 'nimi@yritys.fi',
      phoneLabel: 'Puhelin',
      phonePlaceholder: '+358 40 123 4567',
      messageLabel: 'Viesti',
      messagePlaceholder: 'Kerro lyhyesti yrityksestäsi ja tarpeistasi...',
      submitButton: 'Tilaa JuhlaLaskuri',
      submitting: 'Lähetetään...',
      successMessage: 'Kiitos yhteydenotosta! Otamme sinuun yhteyttä pian.',
      errorMessage: 'Viestin lähetys epäonnistui. Yritä hetken kuluttua uudelleen.',
      
      // Footer
      footerRights: '© 2026 JuhlaLaskuri. Kaikki oikeudet pidätetään.',
      
      // Demo calculator
      demoTitle: 'Pitopalvelulaskuri - Demo',
      eventQuestion: 'Millainen tilaisuus?',
      wedding: 'Häät',
      birthday: 'Syntymäpäivät',
      memorial: 'Muistotilaisuus',
      corporate: 'Yritysjuhlat',
      guestsQuestion: 'Montako vierasta?',
      minGuests: 'Vähintään 10 henkilöä',
      menuChoice: 'Miten rakennetaan menu?',
      menuChoiceDesc: 'Valitse valmis häämenu tai kokoa itse',
      readyMenu: 'Valmis häämenu',
      readyMenuDesc: 'Valitse valmiista menupaketeista',
      customMenu: 'Rakennan itse menun',
      customMenuDesc: 'Valitse ruoat yksitellen',
      selectWeddingMenu: 'Valitse häämenu',
      weddingMenuDesc: 'Valmiit menuvaihtoehdot häihin',
      selectSalads: 'Valitse salaatit',
      selectMultiple: 'Voit valita useamman',
      selectMainCourse: 'Valitse pääruoka',
      dessertQuestion: 'Jälkiruoka?',
      noDessert: 'Ei jälkiruokaa',
      additionalServices: 'Lisäpalvelut',
      deliveryQuestion: 'Toimitus',
      deliveryDesc: 'Toimitetaanko ruoat perille?',
      yes: 'Kyllä',
      no: 'Ei',
      pickup: 'Nouto',
      summary: 'Yhteenveto',
      eventType: 'Tilaisuus',
      guestCount: 'Henkilömäärä',
      persons: 'hlö',
      weddingMenu: 'Häämenu',
      salads: 'Salaatit',
      pcs: 'kpl',
      mainCourse: 'Pääruoka',
      dessert: 'Jälkiruoka',
      delivery: 'Toimitus',
      total: 'Yhteensä',
      demoNote: 'Demo - oikea laskuri räätälöidään sinun hinnoitteluusi',
      demoFooter: 'Laskuri kattaa yleisimmät tilaisuudet – erikoisemmat kokonaisuudet voidaan edelleen hinnoitella tapauskohtaisesti.',
      previous: 'Edellinen',
      next: 'Seuraava',
      startOver: 'Aloita alusta',
      
      // Menu items
      greenSalad: 'Vihersalaatti',
      caesarSalad: 'Caesar-salaatti',
      potatoSalad: 'Perunasalaatti',
      beetrootSalad: 'Punajuurisalaatti',
      porkRoast: 'Porsaanpaisti',
      beefBurgundy: 'Härkää burgundilaisittain',
      chickenThigh: 'Kanan koipireisi',
      salmonLemon: 'Lohta sitruunakastikkeella',
      mochaSquare: 'Mokkapala',
      layerCake: 'Täytekakku',
      cheesecake: 'Juustokakku',
      donutBuffet: 'Munkkibuffa',
      serviceCrew: 'Tarjoiluhenkilökunta',
      tableSetting: 'Pöytienkattaus',
      rentalDishes: 'Vuokra-astiat',
      
      // Wedding menus
      weddingMenu1: 'Häämenu 1 - Klassinen',
      weddingMenu1Desc: 'Perinteinen ja tyylikäs',
      weddingMenu2: 'Häämenu 2 - Moderni',
      weddingMenu2Desc: 'Raikas ja nykyaikainen',
      weddingMenu3: 'Häämenu 3 - Premium',
      weddingMenu3Desc: 'Ylellinen juhla-ateria',
      weddingMenu4: 'Häämenu 4 - Kasvis',
      weddingMenu4Desc: 'Kasvisruokailijan juhlamenu',
      starters: 'Alkupalat',
      mainCourses: 'Pääruoat',
      desserts: 'Jälkiruoat',
      perPerson: '/hlö'
    },
    en: {
      // Navigation
      demo: 'Demo',
      features: 'Features',
      pricing: 'Pricing',
      contact: 'Contact',
      
      // Hero
      badge: 'Catering price calculator',
      heroTitle1: 'Fewer price inquiries,',
      heroTitle2: 'more pre-qualified quote requests.',
      heroDesc1: 'JuhlaLaskuri automates your quote calculations. Your customers build their own menu and see the price instantly – you only receive',
      heroDesc2: 'real orders',
      heroDesc3: ', not pointless inquiries.',
      heroSubtext: 'Handles the most common events automatically – and adapts fully to your own pricing.',
      tryDemo: 'Try demo',
      bookDemo: 'Order JuhlaLaskuri',
      
      // Pain points
      painTitle: 'Does this sound familiar?',
      pain1Title: 'Emails take time',
      pain1Desc: '"How much would a wedding for 50 guests cost?" - Another hour goes to calculating a quote.',
      pain2Title: 'Pointless inquiries',
      pain2Desc: '80% of inquiries lead nowhere - time is wasted.',
      pain3Title: 'Pricing is complicated',
      pain3Desc: 'Every event is different - how to price clearly?',
      painFooter: 'Not all events are the same – that\'s why JuhlaLaskuri focuses on the most common cases and leaves the special ones to you.',
      
      // Solution
      solutionTitle: 'The solution:',
      solutionSubtitle: 'Let customers calculate themselves',
      solutionDesc: 'JuhlaLaskuri is a price calculator customized for your business that',
      solutionBold: 'filters out pointless inquiries.',
      solutionHow: 'How does it work?',
      step1: 'Customer selects food and services from the calculator',
      step2: 'Sees the price instantly - no surprises',
      step3: 'Only serious buyers send a quote request',
      step4: 'You receive a pre-calculated order',
      
      // Target customers
      targetTitle: 'Who is JuhlaLaskuri for?',
      targetFooter: 'Works best for businesses with clear pricing, ready service packages, or recurring customer needs.',
      customer1Title: 'Catering companies',
      customer1Desc: 'Event and order catering',
      customer2Title: 'Venues & Manors',
      customer2Desc: 'Weddings, parties, meetings',
      customer3Title: 'Private dining',
      customer3Desc: 'Private events',
      customer4Title: 'Cafés & Bakeries',
      customer4Desc: 'Order catering and cakes',
      customer5Title: 'Hotels',
      customer5Desc: 'Meeting and event services',
      customer6Title: 'Food services',
      customer6Desc: 'B2B lunch and meeting catering',
      
      // Features
      featuresTitle: 'Why JuhlaLaskuri?',
      feature1Title: 'Save time',
      feature1Desc: 'Automate quote calculations - save an hour per inquiry',
      feature2Title: 'Filter pointless inquiries',
      feature2Desc: 'Customer sees the price immediately and decides whether to continue - no time wasted on pointless inquiries',
      feature3Title: 'Adapts to your pricing',
      feature3Desc: 'The calculator is built entirely based on your menus and prices, so it works like your normal pricing',
      feature4Title: 'Increase sales',
      feature4Desc: 'Easy buying journey = more orders',
      feature5Title: 'Stop the email chaos',
      feature5Desc: 'No more "how much does it cost?" inquiries - price is visible immediately',
      feature6Title: 'Professional impression',
      feature6Desc: 'Modern tool sets you apart from competitors',
      
      // Pricing
      pricingTitle: 'Simple pricing',
      pricingSubtitle: 'No hidden costs, no surprises',
      pricingIntro: 'Setup is fast, and the calculator is built entirely with your products and prices.',
      monthly: 'Monthly',
      yearly: 'Yearly',
      currency: '€',
      perMonth: '/mo',
      perYear: '/yr',
      setupFee: 'Setup & customization',
      setupFeeAmount: '€299',
      pricingIncludes: 'Includes:',
      pricingItem1: 'Customized calculator for your business',
      pricingItem2: 'Your own pricing and menu structure',
      pricingItem3: 'Logos and brand colors',
      pricingItem4: 'Quote requests to email',
      pricingItem5: 'Admin dashboard',
      pricingItem6: 'Technical support',
      getStarted: 'Get started',
      save: 'Save',
      
      // Contact
      contactTitle: 'Interested?',
      contactSubtitle: 'Book a free demo or ask for more info',
      contactIntro: 'Get JuhlaLaskuri with your own menus and pricing – ready to use.',
      nameLabel: 'Name',
      namePlaceholder: 'First Last',
      companyLabel: 'Company',
      companyPlaceholder: 'Company name',
      emailLabel: 'Email',
      emailPlaceholder: 'name@company.com',
      phoneLabel: 'Phone',
      phonePlaceholder: '+358 40 123 4567',
      messageLabel: 'Message',
      messagePlaceholder: 'Tell us briefly about your business and needs...',
      submitButton: 'Order JuhlaLaskuri',
      submitting: 'Sending...',
      successMessage: 'Thank you for contacting us! We will get back to you soon.',
      errorMessage: 'Failed to send message. Please try again later.',
      
      // Footer
      footerRights: '© 2026 JuhlaLaskuri. All rights reserved.',
      
      // Demo calculator
      demoTitle: 'Catering Calculator - Demo',
      eventQuestion: 'What kind of event?',
      wedding: 'Wedding',
      birthday: 'Birthday',
      memorial: 'Memorial',
      corporate: 'Corporate event',
      guestsQuestion: 'How many guests?',
      minGuests: 'Minimum 10 people',
      menuChoice: 'How to build the menu?',
      menuChoiceDesc: 'Choose a ready wedding menu or build your own',
      readyMenu: 'Ready wedding menu',
      readyMenuDesc: 'Choose from ready menu packages',
      customMenu: 'Build my own menu',
      customMenuDesc: 'Select dishes individually',
      selectWeddingMenu: 'Select wedding menu',
      weddingMenuDesc: 'Ready menu options for weddings',
      selectSalads: 'Select salads',
      selectMultiple: 'You can select multiple',
      selectMainCourse: 'Select main course',
      dessertQuestion: 'Dessert?',
      noDessert: 'No dessert',
      additionalServices: 'Additional services',
      deliveryQuestion: 'Delivery',
      deliveryDesc: 'Should we deliver the food?',
      yes: 'Yes',
      no: 'No',
      pickup: 'Pickup',
      summary: 'Summary',
      eventType: 'Event',
      guestCount: 'Guest count',
      persons: 'ppl',
      weddingMenu: 'Wedding menu',
      salads: 'Salads',
      pcs: 'pcs',
      mainCourse: 'Main course',
      dessert: 'Dessert',
      delivery: 'Delivery',
      total: 'Total',
      demoNote: 'Demo - the real calculator is customized to your pricing',
      demoFooter: 'The calculator covers the most common events – special occasions can still be priced case by case.',
      previous: 'Previous',
      next: 'Next',
      startOver: 'Start over',
      
      // Menu items
      greenSalad: 'Green salad',
      caesarSalad: 'Caesar salad',
      potatoSalad: 'Potato salad',
      beetrootSalad: 'Beetroot salad',
      porkRoast: 'Pork roast',
      beefBurgundy: 'Beef Burgundy',
      chickenThigh: 'Chicken thigh',
      salmonLemon: 'Salmon with lemon sauce',
      mochaSquare: 'Mocha square',
      layerCake: 'Layer cake',
      cheesecake: 'Cheesecake',
      donutBuffet: 'Donut buffet',
      serviceCrew: 'Service staff',
      tableSetting: 'Table setting',
      rentalDishes: 'Rental dishes',
      
      // Wedding menus
      weddingMenu1: 'Wedding Menu 1 - Classic',
      weddingMenu1Desc: 'Traditional and elegant',
      weddingMenu2: 'Wedding Menu 2 - Modern',
      weddingMenu2Desc: 'Fresh and contemporary',
      weddingMenu3: 'Wedding Menu 3 - Premium',
      weddingMenu3Desc: 'Luxurious celebration meal',
      weddingMenu4: 'Wedding Menu 4 - Vegetarian',
      weddingMenu4Desc: 'Vegetarian celebration menu',
      starters: 'Starters',
      mainCourses: 'Main courses',
      desserts: 'Desserts',
      perPerson: '/person'
    }
  };

  const text = t[language];

  const targetCustomers = [
    { icon: UtensilsCrossed, title: text.customer1Title, desc: text.customer1Desc },
    { icon: Building2, title: text.customer2Title, desc: text.customer2Desc },
    { icon: ChefHat, title: text.customer3Title, desc: text.customer3Desc },
    { icon: Cake, title: text.customer4Title, desc: text.customer4Desc },
    { icon: Wine, title: text.customer5Title, desc: text.customer5Desc },
    { icon: Users, title: text.customer6Title, desc: text.customer6Desc }
  ];

  const features = [
    { icon: Clock, title: text.feature1Title, desc: text.feature1Desc },
    { icon: Filter, title: text.feature2Title, desc: text.feature2Desc },
    { icon: Calculator, title: text.feature3Title, desc: text.feature3Desc },
    { icon: TrendingUp, title: text.feature4Title, desc: text.feature4Desc },
    { icon: XCircle, title: text.feature5Title, desc: text.feature5Desc },
    { icon: BadgeCheck, title: text.feature6Title, desc: text.feature6Desc }
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: formData.message
        }),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-stone-950 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center">
            <img src={logoUrl} alt="JuhlaLaskuri" className="h-20 w-auto" />
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="#demo" className="text-stone-300 hover:text-amber-400 transition-colors font-medium">{text.demo}</a>
            <a href="#ominaisuudet" className="text-stone-300 hover:text-amber-400 transition-colors font-medium">{text.features}</a>
            <a href="#hinnoittelu" className="text-stone-300 hover:text-amber-400 transition-colors font-medium">{text.pricing}</a>
            <a href="#yhteystiedot">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl px-6">
                {text.contact}
              </Button>
            </a>
            {/* Language Switcher */}
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => setLanguage('fi')}
                className={`w-8 h-6 rounded overflow-hidden border-2 transition-all ${language === 'fi' ? 'border-amber-500 scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                title="Suomi"
              >
                <svg viewBox="0 0 1800 1100" className="w-full h-full">
                  <rect fill="#fff" width="1800" height="1100"/>
                  <rect fill="#003580" y="400" width="1800" height="300"/>
                  <rect fill="#003580" x="500" width="300" height="1100"/>
                </svg>
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`w-8 h-6 rounded overflow-hidden border-2 transition-all ${language === 'en' ? 'border-amber-500 scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                title="English"
              >
                <svg viewBox="0 0 60 30" className="w-full h-full">
                  <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
                  <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
                  <g clipPath="url(#s)">
                    <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
                    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
                    <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
                    <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
                    <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
                  </g>
                </svg>
              </button>
            </div>
          </div>
          {/* Mobile Language Switcher */}
          <div className="flex md:hidden items-center gap-1">
            <button
              onClick={() => setLanguage('fi')}
              className={`w-7 h-5 rounded overflow-hidden border-2 transition-all ${language === 'fi' ? 'border-amber-500' : 'border-transparent opacity-60'}`}
            >
              <svg viewBox="0 0 1800 1100" className="w-full h-full">
                <rect fill="#fff" width="1800" height="1100"/>
                <rect fill="#003580" y="400" width="1800" height="300"/>
                <rect fill="#003580" x="500" width="300" height="1100"/>
              </svg>
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`w-7 h-5 rounded overflow-hidden border-2 transition-all ${language === 'en' ? 'border-amber-500' : 'border-transparent opacity-60'}`}
            >
              <svg viewBox="0 0 60 30" className="w-full h-full">
                <clipPath id="sm"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
                <clipPath id="tm"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
                <g clipPath="url(#sm)">
                  <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
                  <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
                  <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#tm)" stroke="#C8102E" strokeWidth="4"/>
                  <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
                  <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
                </g>
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                {text.badge}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 mb-6 leading-tight">
                {text.heroTitle1} <span className="text-amber-600">{text.heroTitle2}</span>
              </h1>
              <p className="text-base sm:text-lg text-stone-600 mb-4">
                {text.heroDesc1} <span className="text-amber-600 font-semibold">{text.heroDesc2}</span>{text.heroDesc3}
              </p>
              <p className="text-sm sm:text-base text-stone-500 mb-8">
                {text.heroSubtext}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="#demo" className="w-full sm:w-auto">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full shadow-lg shadow-amber-500/25">
                    <Play className="w-5 h-5 mr-2" />
                    {text.tryDemo}
                  </Button>
                </a>
                <a href="#yhteystiedot" className="w-full sm:w-auto">
                  <Button variant="outline" className="border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white rounded-xl px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full transition-all">
                    {text.bookDemo}
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative flex justify-center lg:justify-end mt-8 lg:mt-0">
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-amber-300 rounded-full opacity-20 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-4 -right-4 w-40 h-40 bg-amber-400 rounded-full opacity-20 blur-3xl pointer-events-none" />
              <div className="w-full max-w-md">
                <MiniDemoCalculator language={language} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-stone-900 mb-6">
            {text.painTitle}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { emoji: '📧', title: text.pain1Title, desc: text.pain1Desc },
              { emoji: '⏰', title: text.pain2Title, desc: text.pain2Desc },
              { emoji: '😤', title: text.pain3Title, desc: text.pain3Desc }
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-stone-200">
                <span className="text-4xl mb-3 block">{item.emoji}</span>
                <h3 className="font-semibold text-stone-800 mb-2">{item.title}</h3>
                <p className="text-stone-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-stone-500 text-sm mt-8 max-w-2xl mx-auto">
            {text.painFooter}
          </p>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              {text.solutionHow}
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[text.step1, text.step2, text.step3, text.step4].map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold mb-4 shadow-lg shadow-amber-500/25">
                  {i + 1}
                </div>
                <p className="text-stone-700">{step}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-6 left-full w-full h-0.5 bg-amber-200 -translate-x-6" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Demo Section */}
      <section id="demo" className="py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              {text.tryDemo}
            </h2>
          </div>
          <div className="max-w-md mx-auto">
            <MiniDemoCalculator language={language} />
          </div>
          <p className="text-stone-500 text-sm mt-6 text-center max-w-xl mx-auto">
            {text.demoFooter}
          </p>
          <div className="text-center mt-8">
            <a href="#yhteystiedot">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl px-8 py-4 shadow-lg shadow-amber-500/25">
                {text.getStarted}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Quote / Social Proof */}
      <section className="py-12 bg-gradient-to-r from-amber-500 to-amber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-xl md:text-2xl font-medium text-white leading-relaxed">
            "{language === 'fi' 
              ? 'Hinnan nähnyt ja tarjouspyynnön lähettänyt asiakas on todennäköisemmin oikeasti ostamassa – ei vain kartoittamassa hintoja.'
              : 'A customer who has seen the price and sent a quote request is more likely to actually be buying – not just surveying prices.'}"
          </blockquote>
        </div>
      </section>

      {/* Features */}
      <section id="ominaisuudet" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              {text.featuresTitle}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-stone-200 hover:border-amber-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{feature.title}</h3>
                <p className="text-stone-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Customers */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              {text.targetTitle}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {targetCustomers.map((customer, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-stone-200 flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <customer.icon className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">{customer.title}</h3>
                  <p className="text-sm text-stone-600">{customer.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-stone-500 text-sm mt-8 text-center max-w-2xl mx-auto">
            {text.targetFooter}
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section id="hinnoittelu" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              {text.pricingTitle}
            </h2>
            <p className="text-lg text-stone-600 mb-4">{text.pricingSubtitle}</p>
            <p className="text-stone-500 max-w-xl mx-auto">{text.pricingIntro}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Monthly */}
            <div className="bg-white p-8 rounded-3xl border-2 border-stone-200 hover:border-amber-300 transition-all">
              <h3 className="text-xl font-semibold text-stone-900 mb-2">{text.monthly}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-stone-900">79€</span>
                <span className="text-stone-500">{text.perMonth}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[text.pricingItem1, text.pricingItem2, text.pricingItem3, text.pricingItem4, text.pricingItem5].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-stone-600">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-stone-500 mb-4">+ {text.setupFee} 390€</p>
              <a href="#yhteystiedot">
                <Button variant="outline" className="w-full border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white rounded-xl py-3 transition-all">
                  {text.getStarted}
                </Button>
              </a>
            </div>

            {/* Yearly */}
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-8 rounded-3xl relative overflow-hidden shadow-xl shadow-amber-500/25">
              <div className="absolute top-4 right-4 bg-white text-amber-600 text-xs font-bold px-3 py-1 rounded-full">
                {text.save} 158€
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{text.yearly}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">790€</span>
                <span className="text-amber-100">{text.perYear}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[text.pricingItem1, text.pricingItem2, text.pricingItem3, text.pricingItem4, text.pricingItem6].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-amber-100 mb-4">+ {text.setupFee} 390€</p>
              <a href="#yhteystiedot">
                <Button className="w-full bg-white text-amber-600 hover:bg-amber-50 rounded-xl py-3 font-semibold">
                  {text.getStarted}
                </Button>
              </a>
            </div>
          </div>
          <p className="text-center text-stone-500 mt-8 text-sm">
            {language === 'fi' 
              ? 'Hinnat alv 0%. Aloitus ja räätälöinti sisältää laskurin rakentamisen yrityksenne menuilla, hinnoilla, logoilla ja brändiväreillä sekä asennuksen sivuillenne.'
              : 'Prices excl. VAT. Setup & customization includes building the calculator with your menus, prices, logos and brand colors, plus installation on your website.'}
          </p>
          
          {/* Käyttöönotto päivässä badge */}
          <div className="flex justify-center mt-8">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-full shadow-lg">
              <Zap className="w-5 h-5" />
              <span className="font-semibold text-lg">
                {language === 'fi' ? 'Käyttöönotto päivässä' : 'Setup in one day'}
              </span>
              <Zap className="w-5 h-5" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="ukk" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              {language === 'fi' ? 'Usein kysyttyä JuhlaLaskurista' : 'Frequently Asked Questions'}
            </h2>
          </div>
          <div className="space-y-6">
            {/* FAQ 1 */}
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
              <h3 className="font-semibold text-stone-900 mb-3">
                {language === 'fi' 
                  ? 'Korvaako JuhlaLaskuri nykyiset nettisivumme tai tarjouslomakkeemme?' 
                  : 'Does JuhlaLaskuri replace our current website or quote form?'}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {language === 'fi'
                  ? 'Ei korvaa. JuhlaLaskuri lisätään nykyisille sivuillenne ja se täydentää tarjouslomaketta. Asiakas valitsee ensin tilaisuuden, henkilömäärän, menun ja lisäpalvelut, näkee hinnan tai hinta-arvion ja lähettää sen jälkeen tarjouspyynnön. Teille tulee valmiimpi yhteydenotto ilman ylimääräistä edestakaista sähköpostittelua.'
                  : 'No. JuhlaLaskuri is added to your existing website and complements your quote form. The customer first selects the event, number of guests, menu and additional services, sees the price or estimate, and then sends a quote request. You receive a more complete inquiry without unnecessary back-and-forth emailing.'}
              </p>
            </div>

            {/* FAQ 2 */}
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
              <h3 className="font-semibold text-stone-900 mb-3">
                {language === 'fi' 
                  ? 'Sopiiko JuhlaLaskuri, jos jokainen tilaisuus on erilainen?' 
                  : 'Is JuhlaLaskuri suitable if every event is different?'}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {language === 'fi'
                  ? 'Kyllä. Laskuri kannattaa rakentaa erityisesti yleisimpiin ja toistuviin tilaisuuksiin, kuten häihin, muistotilaisuuksiin, syntymäpäiviin ja kokoustarjoiluihin. Erikoistoiveet, poikkeavat kokonaisuudet ja täysin räätälöidyt juhlat voidaan edelleen hinnoitella tapauskohtaisesti.'
                  : 'Yes. The calculator is best built for the most common and recurring events, such as weddings, memorials, birthdays and meeting catering. Special requests, unique packages and fully customized celebrations can still be priced on a case-by-case basis.'}
              </p>
            </div>

            {/* FAQ 3 */}
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
              <h3 className="font-semibold text-stone-900 mb-3">
                {language === 'fi' 
                  ? 'Pitääkö sivuilla näyttää tarkka ja sitova hinta?' 
                  : 'Do we have to show an exact and binding price?'}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {language === 'fi'
                  ? 'Ei välttämättä. Laskuri voi näyttää esimerkiksi suuntaa-antavan hinta-arvion, alkaen-hinnan tai asiakkaan valinnoista muodostuvan kokonaisuuden. Lopullinen tarjous ja hyväksyntä säilyvät aina teillä.'
                  : 'Not necessarily. The calculator can show, for example, an indicative price estimate, starting price, or a total based on customer selections. The final quote and approval always remain with you.'}
              </p>
            </div>

            {/* FAQ 4 */}
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
              <h3 className="font-semibold text-stone-900 mb-3">
                {language === 'fi' 
                  ? 'Miten laskuri auttaa saamaan parempia tarjouspyyntöjä?' 
                  : 'How does the calculator help get better quote requests?'}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {language === 'fi'
                  ? 'Asiakas näkee hintatason ennen yhteydenottoa ja voi koota tarvitsemansa kokonaisuuden valmiiksi. Kun hän lähettää tarjouspyynnön tämän jälkeen, hän on yleensä jo paremmin perillä budjetista ja todennäköisemmin aidosti kiinnostunut etenemään. Te saatte samalla asiakkaan valinnat valmiiksi koottuna.'
                  : 'The customer sees the price level before contacting you and can assemble their needed package in advance. When they send a quote request after this, they are usually better informed about the budget and more likely genuinely interested in proceeding. You also receive the customer\'s selections pre-assembled.'}
              </p>
            </div>

            {/* FAQ 5 */}
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
              <h3 className="font-semibold text-stone-900 mb-3">
                {language === 'fi' 
                  ? 'Kuinka paljon käyttöönotto vaatii meiltä työtä?' 
                  : 'How much work does the setup require from us?'}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {language === 'fi'
                  ? 'Laskuri rakennetaan yrityksenne omien menujen, hintojen, logon ja brändivärien pohjalta. Teidän tehtäväksenne jää toimittaa tarvittavat menu- ja hintatiedot sekä hyväksyä lopputulos. Tavoitteena on tehdä käyttöönotosta mahdollisimman kevyt.'
                  : 'The calculator is built based on your own menus, prices, logo and brand colors. Your task is to provide the necessary menu and price information and approve the final result. The goal is to make the setup as light as possible.'}
              </p>
            </div>

            {/* FAQ 6 */}
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
              <h3 className="font-semibold text-stone-900 mb-3">
                {language === 'fi' 
                  ? 'Paljonko JuhlaLaskuri maksaa?' 
                  : 'How much does JuhlaLaskuri cost?'}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {language === 'fi'
                  ? 'JuhlaLaskurin kuukausihinta on 79 €/kk tai 790 €/vuosi (alv 0 %). Lisäksi veloitetaan 390 € aloitus- ja räätälöintimaksu, joka sisältää laskurin rakentamisen yrityksenne menuilla, hinnoilla, logoilla ja brändiväreillä sekä asennuksen sivuillenne.'
                  : 'JuhlaLaskuri costs €79/month or €790/year (excl. VAT). Additionally, there is a €390 setup & customization fee, which includes building the calculator with your menus, prices, logos and brand colors, plus installation on your website.'}
              </p>
            </div>
          </div>

          {/* CTA after FAQ */}
          <div className="mt-12 text-center bg-gradient-to-r from-amber-50 to-amber-100 rounded-3xl p-8 border border-amber-200">
            <h3 className="text-xl font-semibold text-stone-900 mb-3">
              {language === 'fi' 
                ? 'Etkö ole varma, sopiiko laskuri teidän hinnoitteluunne?' 
                : 'Not sure if the calculator fits your pricing?'}
            </h3>
            <p className="text-stone-600 mb-6">
              {language === 'fi'
                ? 'Varaa maksuton esittely, niin katsotaan yhdessä, miten JuhlaLaskuri toimisi juuri teidän menuilla ja palveluilla.'
                : 'Book a free demo and let\'s see together how JuhlaLaskuri would work with your menus and services.'}
            </p>
            <a href="#yhteystiedot">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl px-8 py-3 text-lg">
                {language === 'fi' ? 'Varaa esittely' : 'Book a demo'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="yhteystiedot" className="py-20" style={{ backgroundColor: '#FEF3C7' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">
              {text.contactTitle}
            </h2>
            <p className="text-lg text-stone-600 mb-4">
              {text.contactSubtitle}
            </p>
            <p className="text-stone-500 max-w-xl mx-auto">
              {text.contactIntro}
            </p>
          </div>
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{text.nameLabel} *</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="border-2 border-stone-200 rounded-xl focus:border-amber-500"
                    placeholder={text.namePlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{text.companyLabel}</label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="border-2 border-stone-200 rounded-xl focus:border-amber-500"
                    placeholder={text.companyPlaceholder}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{text.emailLabel} *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="border-2 border-stone-200 rounded-xl focus:border-amber-500"
                    placeholder={text.emailPlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{text.phoneLabel}</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="border-2 border-stone-200 rounded-xl focus:border-amber-500"
                    placeholder={text.phonePlaceholder}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">{text.messageLabel}</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="border-2 border-stone-200 rounded-xl focus:border-amber-500"
                  rows={4}
                  placeholder={text.messagePlaceholder}
                />
              </div>
              
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  {text.successMessage}
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                  {text.errorMessage}
                </div>
              )}
              
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl py-4 text-lg shadow-lg shadow-amber-500/25 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>{text.submitting}</>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {text.submitButton}
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-950 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src={logoUrl} alt="JuhlaLaskuri" className="h-32 w-auto" />
            <div className="flex flex-col items-center md:items-end gap-3 text-stone-400">
              <a href="tel:+358442577725" className="hover:text-amber-400 transition-colors flex items-center gap-2">
                <Phone className="w-4 h-4" />
                044 257 7725
              </a>
              <a href="mailto:antti@siira-digital.com" className="hover:text-amber-400 transition-colors flex items-center gap-2">
                <Mail className="w-4 h-4" />
                antti@siira-digital.com
              </a>
            </div>
          </div>
          <div className="border-t border-stone-800 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-stone-500 text-sm">
            <span>{text.footerRights}</span>
            <span className="hidden sm:inline">•</span>
            <a 
              href="https://siiradigital.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-stone-400 hover:text-amber-400 transition-colors"
            >
              Powered by Siira Digital
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default JuhlaLaskuriLandingPage;
